import React, { useState, useEffect, useContext, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import {
  Box, Container, Typography, Button, TextField, CircularProgress,
  Snackbar, Alert, Paper, Divider, InputAdornment, IconButton,
  Switch,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslation } from 'react-i18next';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';
import packageJson from '../../package.json';

const APP_VERSION = packageJson.version;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: '"Outfit", sans-serif',
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: AMBER },
    '& input': { color: '#f1f5f9', fontFamily: '"Outfit", sans-serif' },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #1a3460 inset',
      WebkitTextFillColor: '#f1f5f9',
    },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8', fontFamily: '"Outfit", sans-serif' },
  '& .MuiInputLabel-root.Mui-focused': { color: AMBER },
  '& .MuiInputLabel-shrink': {
    backgroundColor: '#122d62',
    paddingLeft: '4px',
    paddingRight: '4px',
    marginLeft: '-2px',
  },
};

const PreferencesPage = () => {
  const { authTokens, backendUrl, pageTitle, profile } = useContext(AuthContext);
  const { t } = useTranslation();
  document.title = (pageTitle || 'App') + ' - ' + t('preferences.title');

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
  });
  const [notifyPrefs, setNotifyPrefs] = useState({
    admin_notify_route_start: true,
    admin_notify_route_end: true,
    admin_notify_pickup: true,
    admin_notify_skip: true,
    admin_notify_delay: true,
  });
  const [savingNotify, setSavingNotify] = useState(false);
  const [passwords, setPasswords] = useState({ password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', text: '' });

  const authHeader = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + String(authTokens.access),
  }), [authTokens]);

  useEffect(() => {
    fetch(backendUrl + 'api/profile/', { headers: authHeader() })
      .then((r) => r.json())
      .then((data) => {
        setForm({
          first_name: data.user?.first_name || '',
          last_name: data.user?.last_name || '',
          email: data.user?.email || '',
          phone_number: data.phone_number || '',
        });
        setNotifyPrefs({
          admin_notify_route_start: data.admin_notify_route_start ?? true,
          admin_notify_route_end: data.admin_notify_route_end ?? true,
          admin_notify_pickup: data.admin_notify_pickup ?? true,
          admin_notify_skip: data.admin_notify_skip ?? true,
          admin_notify_delay: data.admin_notify_delay ?? true,
        });
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, []);

  const handleChange = (field) => (e) => {
    const value = field === 'phone_number' ? e.target.value.replace(/[^0-9+\- ]/g, '') : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(backendUrl + 'api/profile/', {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setToast({ open: true, severity: 'success', text: t('preferences.saved') });
      } else {
        setToast({ open: true, severity: 'error', text: t('preferences.save_error') });
      }
    } catch {
      setToast({ open: true, severity: 'error', text: t('preferences.save_error') });
    }
    setSaving(false);
  };

  const handleSavePassword = async () => {
    if (!passwords.password) return;
    if (passwords.password !== passwords.confirm) {
      setToast({ open: true, severity: 'error', text: t('preferences.password_mismatch') });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(backendUrl + 'api/profile/', {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify({ password: passwords.password }),
      });
      if (res.ok) {
        setPasswords({ password: '', confirm: '' });
        setToast({ open: true, severity: 'success', text: t('preferences.password_saved') });
      } else {
        setToast({ open: true, severity: 'error', text: t('preferences.save_error') });
      }
    } catch {
      setToast({ open: true, severity: 'error', text: t('preferences.save_error') });
    }
    setSaving(false);
  };

  const handleSaveNotifications = async () => {
    setSavingNotify(true);
    try {
      const res = await fetch(backendUrl + 'api/profile/', {
        method: 'PATCH',
        headers: authHeader(),
        body: JSON.stringify(notifyPrefs),
      });
      if (res.ok) {
        setToast({ open: true, severity: 'success', text: t('preferences.notify_saved') });
      } else {
        setToast({ open: true, severity: 'error', text: t('preferences.save_error') });
      }
    } catch {
      setToast({ open: true, severity: 'error', text: t('preferences.save_error') });
    }
    setSavingNotify(false);
  };

  const sectionPaperSx = {
    background: 'linear-gradient(180deg, #122d62 0%, #0f2550 100%)',
    border: '1px solid rgba(245,158,11,0.18)',
    borderRadius: '16px',
    p: { xs: 3, md: 4 },
    boxShadow: '0 4px 32px rgba(0,0,0,0.35)',
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: AMBER }} />
      </Box>
    );
  }

  return (
    <Box sx={{ background: '#0a1929', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="sm">

        {/* Page header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 700,
              color: '#f1f5f9',
              letterSpacing: '-0.01em',
            }}
          >
            {t('preferences.title')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
            {t('preferences.subtitle')}
          </Typography>
        </Box>

        {/* Profile info section */}
        <Paper sx={{ ...sectionPaperSx, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: '10px',
                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                boxShadow: `0 0 14px ${AMBER_GLOW}`,
              }}
            >
              <PersonIcon sx={{ color: '#080d1a', fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '1rem', color: '#f1f5f9' }}>
              {t('preferences.personal_info')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label={t('preferences.first_name')}
              value={form.first_name}
              onChange={handleChange('first_name')}
              fullWidth
              autoComplete="given-name"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('preferences.last_name')}
              value={form.last_name}
              onChange={handleChange('last_name')}
              fullWidth
              autoComplete="family-name"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('preferences.email')}
              value={form.email}
              onChange={handleChange('email')}
              fullWidth
              autoComplete="email"
              type="email"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('preferences.phone_number')}
              value={form.phone_number}
              onChange={handleChange('phone_number')}
              fullWidth
              autoComplete="tel"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#080d1a' }} /> : <SaveIcon />}
            sx={{
              mt: 3,
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
              color: '#080d1a',
              px: 3,
              py: 1,
              boxShadow: `0 4px 14px ${AMBER_GLOW}`,
              '&:hover': { background: `linear-gradient(135deg, ${AMBER_DARK} 0%, #b45309 100%)` },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {t('preferences.save')}
          </Button>
        </Paper>

        {/* Notification preferences section */}
        <Paper sx={{ ...sectionPaperSx, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: '10px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 0 14px rgba(16,185,129,0.25)',
              }}
            >
              <NotificationsIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '1rem', color: '#f1f5f9' }}>
              {t('preferences.notifications')}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#64748b', mb: 2.5 }}>
            {t('preferences.notifications_subtitle')}
          </Typography>

          {[
            { key: 'admin_notify_route_start', label: t('preferences.notify_route_start'), desc: t('preferences.notify_route_start_desc') },
            { key: 'admin_notify_route_end',   label: t('preferences.notify_route_end'),   desc: t('preferences.notify_route_end_desc') },
            { key: 'admin_notify_pickup',      label: t('preferences.notify_pickup'),       desc: t('preferences.notify_pickup_desc') },
            { key: 'admin_notify_skip',        label: t('preferences.notify_skip'),         desc: t('preferences.notify_skip_desc') },
            { key: 'admin_notify_delay',       label: t('preferences.notify_delay'),        desc: t('preferences.notify_delay_desc') },
          ].map(({ key, label, desc }, idx, arr) => (
            <Box key={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                <Box>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', color: '#e2e8f0', fontWeight: 500 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#64748b', mt: 0.25 }}>
                    {desc}
                  </Typography>
                </Box>
                <Switch
                  checked={notifyPrefs[key]}
                  onChange={(e) => setNotifyPrefs((p) => ({ ...p, [key]: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10b981' },
                  }}
                />
              </Box>
              {idx < arr.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />}
            </Box>
          ))}

          <Button
            onClick={handleSaveNotifications}
            disabled={savingNotify}
            variant="contained"
            startIcon={savingNotify ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
            sx={{
              mt: 3,
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              px: 3,
              py: 1,
              boxShadow: '0 4px 14px rgba(16,185,129,0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {t('preferences.save')}
          </Button>
        </Paper>

        {/* Change password section */}
        <Paper sx={sectionPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: '10px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                boxShadow: '0 0 14px rgba(59,130,246,0.25)',
              }}
            >
              <LockIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '1rem', color: '#f1f5f9' }}>
              {t('preferences.change_password')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label={t('preferences.new_password')}
              type={showPassword ? 'text' : 'password'}
              value={passwords.password}
              onChange={(e) => setPasswords((p) => ({ ...p, password: e.target.value }))}
              fullWidth
              autoComplete="new-password"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" sx={{ color: '#64748b' }}>
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('preferences.confirm_password')}
              type={showConfirm ? 'text' : 'password'}
              value={passwords.confirm}
              onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
              fullWidth
              autoComplete="new-password"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" sx={{ color: '#64748b' }}>
                      {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Button
            onClick={handleSavePassword}
            disabled={saving || !passwords.password}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
            sx={{
              mt: 3,
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: '#fff',
              px: 3,
              py: 1,
              boxShadow: '0 4px 14px rgba(59,130,246,0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)' },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {t('preferences.update_password')}
          </Button>
        </Paper>

        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#475569', textAlign: 'center', mt: 3 }}>
          {t('common.app_version', { version: APP_VERSION })}
        </Typography>
      </Container>

      <Snackbar
        open={toast.open}
        autoHideDuration={20000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: '10px' }}
        >
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PreferencesPage;
